/*
 * Creative Cycles Workshop, Showroom home behaviour.
 *
 * <ccw-slides>    hero carousel: arrows, dots, pause, auto-advance. Auto-advance starts after the
 *                 first interaction, stops on hover, on focus inside, for good once paused, and
 *                 never runs under prefers-reduced-motion.
 * <ccw-scroller>  sideways scroll-snap list with arrows and dots that follow the scroll position.
 * [data-ccw-talk] shows the Talk to the Workshop pill once the first screen has scrolled by.
 *
 * Loaded with defer from each section that needs it; the guards keep a second load harmless.
 */
(() => {
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const TALK_SHOW_AFTER_PX = 400;
  const INTERACTIONS = ['pointerdown', 'keydown', 'scroll'];

  class CcwSlides extends HTMLElement {
    connectedCallback() {
      this.slides = [...this.querySelectorAll('[data-slide]')];
      this.dots = [...this.querySelectorAll('[data-dot]')];
      this.pauseButton = this.querySelector('[data-pause]');
      this.index = 0;
      this.paused = false;
      this.held = false;
      this.interval = Number(this.dataset.interval || 7) * 1000;
      if (this.slides.length < 2) return;

      this.querySelector('[data-prev]')?.addEventListener('click', () => this.go(this.index - 1));
      this.querySelector('[data-next]')?.addEventListener('click', () => this.go(this.index + 1));
      this.dots.forEach((dot, i) => dot.addEventListener('click', () => this.go(i)));
      this.pauseButton?.addEventListener('click', () => this.setPaused(!this.paused));

      this.addEventListener('mouseenter', () => this.hold(true));
      this.addEventListener('mouseleave', () => this.hold(false));
      this.addEventListener('focusin', () => this.hold(true));
      this.addEventListener('focusout', (event) => {
        if (!this.contains(event.relatedTarget)) this.hold(false);
      });

      if (reducedMotion.matches) this.setPaused(true);

      // Auto-advance waits for the first interaction. A slide fading in counts as a new largest
      // paint, so advancing on a timer alone would push the page's LCP out to 7 s and beyond.
      this.armed = false;
      const arm = () => {
        if (this.armed) return;
        this.armed = true;
        INTERACTIONS.forEach((type) => window.removeEventListener(type, arm));
        this.schedule();
      };
      INTERACTIONS.forEach((type) => window.addEventListener(type, arm, { passive: true, once: true }));
    }

    disconnectedCallback() {
      clearTimeout(this.timer);
    }

    go(next) {
      const count = this.slides.length;
      this.index = (next + count) % count;
      this.slides.forEach((slide, i) => {
        const active = i === this.index;
        slide.toggleAttribute('data-active', active);
        slide.toggleAttribute('inert', !active);
        if (active) slide.removeAttribute('aria-hidden');
        else slide.setAttribute('aria-hidden', 'true');
      });
      this.dots.forEach((dot, i) => {
        if (i === this.index) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });
      this.schedule();
    }

    hold(value) {
      this.held = value;
      this.schedule();
    }

    setPaused(value) {
      this.paused = value;
      if (this.pauseButton) {
        this.pauseButton.setAttribute('aria-pressed', String(value));
        this.pauseButton.setAttribute('aria-label', value ? 'Play slides' : 'Pause slides');
        this.pauseButton.classList.toggle('is-paused', value);
      }
      this.schedule();
    }

    schedule() {
      clearTimeout(this.timer);
      if (!this.armed || this.paused || this.held) return;
      this.timer = setTimeout(() => this.go(this.index + 1), this.interval);
    }
  }

  class CcwScroller extends HTMLElement {
    connectedCallback() {
      this.list = this.querySelector('[data-list]');
      this.dotsHost = this.querySelector('[data-dots]');
      if (!this.list) return;
      this.items = [...this.list.children];

      this.prev = this.querySelector('[data-prev]');
      this.next = this.querySelector('[data-next]');
      this.prev?.addEventListener('click', () => this.step(-1));
      this.next?.addEventListener('click', () => this.step(1));

      if (this.dotsHost && this.items.length > 1) {
        this.dots = this.items.map((item, i) => {
          const dot = document.createElement('button');
          dot.type = 'button';
          dot.className = 'ccw-dot ccw-dot--ink';
          dot.setAttribute('aria-label', `Show item ${i + 1} of ${this.items.length}`);
          dot.addEventListener('click', () => this.scrollToItem(i));
          this.dotsHost.append(dot);
          return dot;
        });
      }

      // One update per frame, however many scroll events arrive
      let queued = false;
      this.list.addEventListener('scroll', () => {
        if (queued) return;
        queued = true;
        requestAnimationFrame(() => { queued = false; this.update(); });
      }, { passive: true });
      this.update();
    }

    currentIndex() {
      const left = this.list.scrollLeft;
      let best = 0;
      this.items.forEach((item, i) => {
        if (Math.abs(item.offsetLeft - this.list.offsetLeft - left) <
            Math.abs(this.items[best].offsetLeft - this.list.offsetLeft - left)) best = i;
      });
      return best;
    }

    scrollToItem(i) {
      const item = this.items[Math.max(0, Math.min(i, this.items.length - 1))];
      this.list.scrollTo({
        left: item.offsetLeft - this.list.offsetLeft,
        behavior: reducedMotion.matches ? 'auto' : 'smooth',
      });
    }

    step(direction) {
      const arrow = direction < 0 ? this.prev : this.next;
      if (arrow?.getAttribute('aria-disabled') === 'true') return;
      this.scrollToItem(this.currentIndex() + direction);
    }

    update() {
      const current = this.currentIndex();
      this.dots?.forEach((dot, i) => {
        if (i === current) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });
      const max = this.list.scrollWidth - this.list.clientWidth - 2;
      const atStart = this.list.scrollLeft <= 2;
      const atEnd = this.list.scrollLeft >= max;
      this.toggleAttribute('data-at-start', atStart);
      this.toggleAttribute('data-at-end', atEnd);
      // aria-disabled, not disabled: a disabled button drops keyboard focus to the page
      this.prev?.setAttribute('aria-disabled', String(atStart));
      this.next?.setAttribute('aria-disabled', String(atEnd));
    }
  }

  if (!customElements.get('ccw-slides')) customElements.define('ccw-slides', CcwSlides);
  if (!customElements.get('ccw-scroller')) customElements.define('ccw-scroller', CcwScroller);

  if (!window.ccwTalkBound) {
    window.ccwTalkBound = true;
    const showTalk = () => {
      const visible = window.scrollY > TALK_SHOW_AFTER_PX;
      document.querySelectorAll('[data-ccw-talk]').forEach((el) => el.toggleAttribute('data-visible', visible));
    };
    let talkQueued = false;
    window.addEventListener('scroll', () => {
      if (talkQueued) return;
      talkQueued = true;
      requestAnimationFrame(() => { talkQueued = false; showTalk(); });
    }, { passive: true });
    document.addEventListener('DOMContentLoaded', showTalk);
    showTalk();
  }
})();
